import { ApiError, ApiNetworkError, adminAuthLogin } from '@cortex/web-api/clients/admin/api';
import { Wordmark } from '@cortex/web-api/ui/admin/Wordmark';
import { type FormEvent, useState } from 'react';

/**
 * Issue #1090 admin login — shared username/password. The server stores only
 * a bcrypt hash and returns one generic failure for unknown usernames and
 * wrong passwords.
 */

interface Props {
  onVerified: () => void;
  /** Optional preamble shown above the form, e.g. "session expired". */
  notice?: string | null;
}

export function LoginScreen({ onVerified, notice }: Props) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    const trimmedUsername = username.trim();
    if (!trimmedUsername || !password) {
      setError('Enter your username and password.');
      return;
    }
    setBusy(true);
    try {
      const res = await adminAuthLogin(trimmedUsername, password);
      if (res.status === 'authenticated') {
        onVerified();
        return;
      }
      setError('Invalid username or password.');
    } catch (err) {
      setError(messageForError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app">
      <header className="app-header">
        <img src="/acel-cortex-mark.svg" alt="" width={32} height={32} />
        <Wordmark />
      </header>
      <main className="app-main">
        <div className="center-stack">
          <div className="card" style={{ padding: 'var(--space-8)', maxWidth: 360, width: '100%' }}>
            {notice && (
              <p className="field-help" style={{ marginBottom: 'var(--space-4)' }}>
                {notice}
              </p>
            )}
            <form onSubmit={handleSubmit} className="stack" noValidate>
              <div className="stack-tight">
                <h2 className="h3" style={{ color: 'var(--ink)' }}>
                  sign in
                </h2>
              </div>
              <div className="stack-tight">
                <label className="field-label" htmlFor="username">
                  username
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  // biome-ignore lint/a11y/noAutofocus: focus on the first field is the right call here for a single-screen sign-in
                  autoFocus
                  className="field-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={busy}
                  aria-describedby={error ? 'login-error' : undefined}
                  required
                />
              </div>
              <div className="stack-tight">
                <label className="field-label" htmlFor="password">
                  password
                </label>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    className="field-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={busy}
                    aria-describedby={error ? 'login-error' : undefined}
                    required
                  />
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowPassword((v) => !v)}
                    disabled={busy}
                    aria-pressed={showPassword}
                    style={{ padding: '0 12px', minWidth: 64 }}
                  >
                    {showPassword ? 'hide' : 'show'}
                  </button>
                </div>
              </div>
              {error && (
                <p id="login-error" className="field-error" role="alert">
                  {error}
                </p>
              )}
              <button type="submit" className="btn-primary" disabled={busy}>
                {busy ? 'signing in...' : 'sign in'}
              </button>
            </form>
          </div>
        </div>
      </main>
      <footer className="app-footer">acel·cortex internal</footer>
    </div>
  );
}

function messageForError(err: unknown): string {
  if (err instanceof ApiNetworkError) return "Couldn't reach the server. Check your connection.";
  if (err instanceof ApiError) {
    if (err.status === 400) return 'Invalid input. Try again.';
    if (err.status === 401 || err.status === 429) return 'Invalid username or password.';
    if (err.status === 503) return 'Admin sign-in is not configured.';
    return 'Something went wrong. Try again in a moment.';
  }
  return 'Something went wrong.';
}
